import React, { useReducer, createContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SolutionOutlined, DeliveredProcedureOutlined, BlockOutlined, FileSearchOutlined } from '@ant-design/icons';

// Mutation Types...
const APP_LAYOUT_MUTATION_TYPES = {
    SET_SELECTED_SIDEBAR_MENU_ITEM: 'SET_SELECTED_SIDEBAR_MENU_ITEM',
};

// State...
type StateType = {
    sidebarMenuItems: {
        key: string;
        pathToName: string;
        icon: React.ReactElement;
        className: string;
        text: string;
    }[];
    sidebarSelectedMenuItemKey: string;
};

/**
 * Here you can add/config the props for the AppLayout state
 */
const initialState: StateType = {
    sidebarMenuItems: [
        {
            key: 'clients',
            pathToName: '/admin/clients',
            icon: <SolutionOutlined />,
            className: 'd-flex align-items-center',
            text: 'Clients',
        },
        {
            key: 'jobs',
            pathToName: '/admin/jobs',
            icon: <DeliveredProcedureOutlined />,
            className: 'd-flex align-items-center',
            text: 'Jobs',
        },
        {
            key: 'stacks',
            pathToName: '/admin/stacks',
            icon: <BlockOutlined />,
            className: 'd-flex align-items-center',
            text: 'Stacks',
        },
        {
            key: 'logs',
            pathToName: '/admin/logs',
            icon: <FileSearchOutlined />,
            className: 'd-flex align-items-center',
            text: 'Logs',
        },
    ],
    sidebarSelectedMenuItemKey: '',
};

// Context...
type ContextTye = {
    state: StateType;
    dispatch: React.Dispatch<{ mutationType: string; payload?: any }>;
};

const AppLayoutContext = createContext<ContextTye>({
    state: initialState,
    dispatch: () => null,
});

// Reducer...
const reducer = (state: StateType, { mutationType, payload }: { mutationType: string; payload?: any }): StateType => {
    switch (mutationType) {
        case APP_LAYOUT_MUTATION_TYPES.SET_SELECTED_SIDEBAR_MENU_ITEM: {
            const { sidebarMenuItems, sidebarSelectedMenuItemKey } = state;
            const menuItemToSelect = sidebarMenuItems.find((menuItem) => payload.includes(menuItem.pathToName));
            return {
                ...state,
                sidebarSelectedMenuItemKey: menuItemToSelect?.key || sidebarSelectedMenuItemKey,
            };
        }
        default:
            return state;
    }
};

// Provider component...
const AppLayoutProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const history = useHistory();

    const setMenuItemSelectedKey = (pathName: string): void => {
        dispatch({
            mutationType: APP_LAYOUT_MUTATION_TYPES.SET_SELECTED_SIDEBAR_MENU_ITEM,
            payload: pathName,
        });
    };

    useEffect(() => {
        setMenuItemSelectedKey(history.location.pathname);
        return history.listen((location) => {
            setMenuItemSelectedKey(location.pathname);
        });
    }, [history]);

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    return <AppLayoutContext.Provider value={{ state, dispatch }}>{children}</AppLayoutContext.Provider>;
};

export { AppLayoutContext, AppLayoutProvider, APP_LAYOUT_MUTATION_TYPES };
